import { Link } from "@tanstack/react-router";
import { SafeImage } from "@/components/site/SafeImage";
import { Plane, Briefcase, Wallet, Receipt, Info, Mail, Shield, Target, HeartPulse, Landmark, CalendarDays, Laptop, CreditCard, Home as HomeIcon, LineChart, Trophy, Building2, BookOpenCheck, ScrollText, AlertTriangle, Image as ImageIcon, ClipboardCheck } from "lucide-react";
import { COUNTRIES } from "@/data/countries";
import { FollowNotifications } from "@/components/site/FollowNotifications";
import logoAsset from "@/assets/lifewestway-logo-160.webp";




// Data for icon-badge link columns; each entry drives label + tooltip + aria-label.
const TOPIC_LINKS = [
  { to: "/immigration" as const, label: "Immigration", tip: "Visas, PR pathways & citizenship guides", icon: Plane },
  { to: "/jobs" as const,        label: "Jobs",        tip: "Job market intel, salaries & hiring trends", icon: Briefcase },
  { to: "/finance" as const,     label: "Finance",     tip: "Banking, credit & personal finance abroad", icon: Wallet },
  { to: "/tax" as const,         label: "Tax",         tip: "Tax residency, filing rules & optimisation", icon: Receipt },
  { to: "/healthcare" as const,  label: "Health Care", tip: "Health care & insurance guides for all 6 countries", icon: HeartPulse },
  { to: "/cross-border-wealth" as const, label: "CB Wealth & Offshore Banking", tip: "Cross-border wealth & offshore banking disclosure guides (2026)", icon: Landmark },
  { to: "/events" as const, label: "Events", tip: "Country events calendar with dates, venues and ticket links", icon: CalendarDays },
  { to: "/visa-guides-2026" as const, label: "Visa Guides 2026", tip: "Curated 2026 visa guides: Portugal, Canada, UK & Australia", icon: BookOpenCheck },
  { to: "/remote-work" as const, label: "Remote Work", tip: "Remote work & job guides across all 6 countries", icon: Laptop },
  { to: "/business" as const, label: "Business", tip: "Business setup abroad — company registration, founder visas, tax & banking", icon: Building2 },
  { to: "/topics" as const, label: "All Topics", tip: "Every topic hub: insurance, legal, tax filing and AI automation guides", icon: BookOpenCheck },
  { to: "/latest" as const, label: "Latest Guides", tip: "Newest LifeWestWay guides and updates", icon: ScrollText },
  { to: "/quick-guides" as const, label: "Quick Guides", tip: "Short summaries of quick-read expat guides", icon: ScrollText },
  { to: "/currency-hedging-expats-2026" as const, label: "Currency Hedging", tip: "Currency hedging strategies for expats in 2026", icon: LineChart },
];


const COMPANY_LINKS = [
  { to: "/about" as const,   label: "About Us",      tip: "Learn about LifeWestWay's editorial team", icon: Info },
  { to: "/mission-vision" as const, label: "Mission & Vision", tip: "Why LifeWestWay exists and what we stand for", icon: Target },
  { to: "/editorial-process" as const, label: "Editorial Process", tip: "How we research, review and update our guides", icon: ClipboardCheck },
  { to: "/contact" as const, label: "Contact Us",    tip: "Get in touch with our team",              icon: Mail },
  { to: "/privacy" as const, label: "Privacy Policy",tip: "How we handle your data",                  icon: Shield },
  { to: "/terms" as const,   label: "Terms",         tip: "Terms of use for LifeWestWay",             icon: ScrollText },
  { to: "/disclaimer" as const, label: "Disclaimer", tip: "Important limitations of LifeWestWay's content", icon: AlertTriangle },
  { to: "/media-credits" as const, label: "Media Credits", tip: "Photo licensing and attribution",    icon: ImageIcon },
  { to: "/credit-cards" as const, label: "Credit Cards", tip: "Best newcomer & expat credit cards by country", icon: CreditCard },
  { to: "/housing-living-cost" as const, label: "Living Cost", tip: "Rent, deposits, bills & monthly budgets by country", icon: HomeIcon },
  { to: "/investment-money-transfer" as const, label: "Investment", tip: "Investing abroad & cheapest cross-border transfers", icon: LineChart },
  { to: "/success-stories" as const, label: "Success Stories", tip: "Real expat visa, job & PR success journeys", icon: Trophy },
];


export function Footer() {
  return (
    <footer className="relative mt-24 overflow-hidden bg-navy text-white">
      {/* Ambient gold glow */}
      <div className="pointer-events-none absolute inset-0 -z-0" aria-hidden>
        <div className="absolute -top-40 left-1/4 h-96 w-96 rounded-full bg-accent/10 blur-3xl" />
        <div className="absolute -bottom-40 right-0 h-[500px] w-[500px] rounded-full bg-accent/5 blur-3xl" />
        <div
          className="absolute inset-0 opacity-[0.05]"
          style={{
            backgroundImage:
              "radial-gradient(circle at 1px 1px, var(--color-accent) 1px, transparent 0)",
            backgroundSize: "24px 24px",
          }}
        />
      </div>

      {/* Giant watermark */}
      <div
        aria-hidden
        className="pointer-events-none absolute inset-x-0 bottom-0 -z-0 flex select-none justify-center overflow-hidden"
      >
        <span className="translate-y-6 whitespace-nowrap font-display text-[22vw] font-black leading-none tracking-tighter text-white/[0.04] sm:text-[18vw]">
          LifeWestWay
        </span>
      </div>

      <div className="relative z-10 mx-auto max-w-7xl px-4 py-16 sm:px-6 lg:px-8">
        <div className="mb-12">
          <FollowNotifications />
        </div>


        <div className="grid gap-10 border-b border-white/10 pb-12 md:grid-cols-[1.4fr_1fr] md:items-center">
          <div>
            <div className="flex items-center gap-3">
              <span className="relative grid h-20 w-20 sm:h-24 sm:w-24 shrink-0 place-items-center">
              <SafeImage
                src={logoAsset}
                alt="LifeWestWay eagle logo"
                width={96}
                height={96}
                loading="lazy" decoding="async"
                noSkeleton
                className="relative h-full w-full object-contain"
              />
              </span>

              <div>
                <p className="font-display text-4xl sm:text-5xl font-black tracking-tight leading-none">
                  LifeWest<em className="not-italic text-accent">Way</em>
                </p>
                <p className="mt-2 text-[11px] sm:text-sm font-semibold uppercase tracking-[0.18em] sm:tracking-[0.28em] text-accent/80 whitespace-nowrap">
                  Western Migration · Est. 2026
                </p>
              </div>
            </div>
            <p className="mt-5 max-w-xl text-sm leading-relaxed text-white/70">
              Your ultimate guide to Western migration, careers, tax, and lifestyle —
              researched, human-edited, and updated for six top destinations.
            </p>
          </div>

          <div className="hidden md:block" />

        </div>

        {/* Link columns */}
        <div className="mt-12 grid gap-10 sm:grid-cols-2 lg:grid-cols-3">
          <div>
            <h3 className="text-xs font-semibold uppercase tracking-[0.22em] text-accent">
              Countries
            </h3>
            <ul className="mt-5 grid grid-cols-2 gap-x-4 gap-y-2.5 text-sm">
              {COUNTRIES.map((c) => {
                const tip = `Explore the ${c.name} guide — visas, jobs, tax & living`;
                return (
                  <li key={c.slug}>
                    <Link
                      to={"/$country"}
                      params={{ country: c.slug }}
                      title={tip}
                      className="group foot-link py-2"
                    >
                      <SafeImage
                        src={`https://flagcdn.com/w40/${c.code}.webp`}
                        alt={`${c.name} flag`}
                        width={20}
                        height={14}
                        loading="lazy" decoding="async"
                        className="h-4 w-6 rounded-sm object-cover ring-1 ring-white/20 transition-transform group-hover:scale-110"
                      />
                      <span>{c.shortName}</span>
                    </Link>
                  </li>
                );
              })}
            </ul>
          </div>

          <div>
            <h3 className="text-xs font-semibold uppercase tracking-[0.22em] text-accent">
              Topics
            </h3>
            <ul className="mt-5 grid grid-cols-2 gap-x-4 gap-y-2.5 text-sm">
              {TOPIC_LINKS.map(({ to, label, tip, icon: Icon }) => (
                <li key={to}>
                  <Link to={to} title={tip} className="group foot-link py-2">
                    <Icon aria-hidden className="h-4 w-4 shrink-0 text-accent transition-transform group-hover:scale-110" />
                    <span>{label}</span>
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          <div>
            <h3 className="text-xs font-semibold uppercase tracking-[0.22em] text-accent">
              Company
            </h3>
            <ul className="mt-5 grid grid-cols-2 gap-x-4 gap-y-2.5 text-sm">
              {COMPANY_LINKS.map(({ to, label, tip, icon: Icon }) => (
                <li key={to}>
                  <Link to={to} title={tip} className="group foot-link py-2">
                    <Icon aria-hidden className="h-4 w-4 shrink-0 text-accent transition-transform group-hover:scale-110" />
                    <span>{label}</span>
                  </Link>
                </li>
              ))}
            </ul>

          </div>
        </div>


        {/* Utility strip — legal & site pages reachable from every page for crawlers */}
        <nav
          aria-label="Site information"
          className="mt-12 flex flex-wrap items-center gap-x-5 gap-y-2 border-t border-white/10 pt-6 text-xs"
        >
          <Link
            to="/search"
            aria-label="Search all LifeWestWay guides"
            title="Search all LifeWestWay guides"
            className="foot-legal-link"
          >
            Search
          </Link>
          <Link to="/about" title="About LifeWestWay" className="foot-legal-link">
            About
          </Link>
          <Link to="/contact" title="Contact LifeWestWay" className="foot-legal-link">
            Contact
          </Link>
          <Link to="/privacy" title="LifeWestWay Privacy Policy" className="foot-legal-link">
            Privacy Policy
          </Link>
          <Link to="/terms" title="LifeWestWay Terms of Use" className="foot-legal-link">
            Terms
          </Link>
          <Link to="/disclaimer" title="LifeWestWay Disclaimer" className="foot-legal-link">
            Disclaimer
          </Link>
          <Link to="/editorial-process" title="LifeWestWay Editorial Process" className="foot-legal-link">
            Editorial Process
          </Link>
        </nav>

        {/* Bottom bar */}
        <div className="mt-6 flex flex-col items-start justify-between gap-3 border-t border-white/10 pt-6 md:flex-row md:items-center">
          <p className="text-sm leading-relaxed text-white/70">
            © 2026 <strong className="font-semibold text-white/90">LifeWestWay</strong>. All rights reserved.
          </p>
          <p className="text-sm leading-relaxed text-white/70">
            Independent editorial. Content is informational and not legal, tax, or immigration advice.
          </p>
        </div>
      </div>
    </footer>
  );
}
